import React from "react";
import { Helmet } from "react-helmet";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import DashboardLayout from "@/components/DashboardLayout";

const Privacy = () => {
  return (
    <DashboardLayout>
      <div className="container mx-auto py-12 px-4">
        <Helmet>
          <title>Privacy Policy | OpenPipe</title>
        </Helmet>
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="text-3xl font-bold">Privacy Policy</CardTitle>
            <CardDescription>Last updated December 12, 2024</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              This privacy notice for OpenPipe, Inc. ("Company," "we," "us," or
              "our"), describes how and why we might collect, store, use, and/or
              share ("process") your information when you use our services
              ("Services"), such as when you:
            </p>

            <ul>
              <li>
                Visit our website at https://openpipe.ai, or any website of ours
                that links to this privacy notice
              </li>
              <li>
                Use the OpenPipe online service or any of our associated tools
                and SDKs
              </li>
              <li>
                Engage with us in other related ways ― including any advisory
                meetings, mentor sessions, sales, marketing, or events such as
                webinars
              </li>
            </ul>

            <p>
              <strong>Questions or concerns?</strong> Reading this privacy
              notice will help you understand your privacy rights and choices.
              If you do not agree with our policies and practices, please do not
              use our Services. If you still have any questions or concerns,
              please contact us at:
            </p>

            <p>Email: notices@openpipe.ai</p>

            <p>
              EU representative: Adam Brogden, contact@gdprlocal.com, + 353 15
              549 700, INSTANT EU GDPR REPRESENTATIVE LIMITED Office 2 12A Lower
              Main Street, Lucan Co. Dublin K78 X5P8 Ireland
            </p>

            <p>DPO: Kyle Corbitt, CEO of OpenPipe</p>

            <h2>SUMMARY OF KEY POINTS</h2>

            <p>
              <strong>What personal information do we process?</strong> When you
              visit, use, or navigate our Services, we may process personal
              information depending on how you interact with OpenPipe and the
              Services, the choices you make, and the products and features you
              use.
            </p>

            <p>
              We may process sensitive personal information when necessary with
              your consent or as otherwise permitted by applicable law. This
              personal information is largely limited to your name, contact
              information (such as your email address and phone number), payment
              information, and information related to our Services.
            </p>

            <p>
              We may receive information from public databases, marketing
              partners, social media platforms, and other outside sources.
            </p>

            <p>
              We process your information to provide, improve, and administer
              our Services, communicate with you, for security and fraud
              prevention, and to comply with law. We may also process your
              information for other purposes with your consent. We process your
              information only when we have a valid legal reason to do so.
            </p>

            <p>
              We may share information in specific situations and with specific
              categories of third parties.
            </p>

            <p>
              We have organizational and technical processes and procedures in
              place to protect your personal information. However, no electronic
              transmission over the internet or information storage technology
              can be guaranteed to be 100% secure, so we cannot promise or
              guarantee that hackers, cybercriminals, or other unauthorized
              third parties will not be able to defeat our security and
              improperly collect, access, steal, or modify your information.
            </p>

            <p>
              Depending on where you are located geographically, the applicable
              privacy law may mean you have certain rights regarding your
              personal information.
            </p>

            <p>
              The easiest way to exercise your rights is by sending a data
              subject request to notices@openpipe.ai. We will consider and act
              upon any request in accordance with applicable data protection
              laws.
            </p>

            <p>
              Want to learn more about what OpenPipe does with any information
              we collect? Review the notice in full below.
            </p>

            <h2>TABLE OF CONTENTS</h2>
            <ol className="toc">
              <li>
                <a href="#section1">WHAT INFORMATION DO WE COLLECT?</a>
              </li>
              <li>
                <a href="#section2">HOW DO WE PROCESS YOUR INFORMATION?</a>
              </li>
              <li>
                <a href="#section3">
                  WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
                </a>
              </li>
              <li>
                <a href="#section4">
                  IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?
                </a>
              </li>
              <li>
                <a href="#section5">HOW LONG DO WE KEEP YOUR INFORMATION?</a>
              </li>
              <li>
                <a href="#section6">HOW DO WE KEEP YOUR INFORMATION SAFE?</a>
              </li>
              <li>
                <a href="#section7">DO WE COLLECT INFORMATION FROM MINORS?</a>
              </li>
              <li>
                <a href="#section8">WHAT ARE YOUR PRIVACY RIGHTS?</a>
              </li>
              <li>
                <a href="#section9">CONTROLS FOR DO-NOT-TRACK FEATURES</a>
              </li>
              <li>
                <a href="#section10">OPENPIPE AND STATE LAWS</a>
              </li>
              <li>
                <a href="#section11">DO WE MAKE UPDATES TO THIS NOTICE?</a>
              </li>
              <li>
                <a href="#section12">
                  HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
                </a>
              </li>
              <li>
                <a href="#section13">
                  HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
                  YOU?
                </a>
              </li>
              <li>
                <a href="#section14">MISCELLANEOUS</a>
              </li>
            </ol>

            <h2 id="section1">1. WHAT INFORMATION DO WE COLLECT?</h2>
            <p>
              We collect personal information that you voluntarily provide to us
              when you express an interest in our Services, such as learning
              about us or our Services.
            </p>

            <p>
              <strong>Personal Information Provided by You.</strong> The
              personal information that we collect depends on the context of
              your interactions with us and the Services, the choices you make,
              and the products and features you use. The personal information we
              collect may include the following:
            </p>

            <ul>
              <li>Your first and last name</li>
              <li>Your email address</li>
              <li>Your phone number</li>
              <li>
                Any personal information you explicitly include in the datasets
                you upload to our service or requests you log.
              </li>
            </ul>

            <p>
              <strong>Payment Data.</strong> We may collect data necessary to
              process your payment if you make purchases, such as your payment
              instrument number (such as a credit card number), and the security
              code associated with your payment instrument. All payment data is
              stored by Stripe, Inc. You may find their privacy notice link(s)
              here: https://stripe.com/privacy. Stripe also covers instances
              where financing through Affirm or Klarna is used.
            </p>

            <p>
              All personal information that you provide to us must be true,
              complete, and accurate, and you must notify us of any changes to
              such personal information.
            </p>

            <p>
              <strong>Customer Data.</strong> We collect data from you (the
              "Customer") that you voluntarily provide to us, or create through
              use of our Services, to deliver and facilitate delivery of
              Services ("Customer Data"). "Training Data" is a subset of
              Customer Data and includes customer supplied training datasets,
              large language model traces, and other data specifically supplied
              for the training of large language models. "Custom Models" are a
              subset of Customer Data and include any large language model that
              has been trained by Customer using Customer's Training Data.
              Customer Data is classified as confidential, and ownership of such
              data remains with you at all times. We securely process collected
              Customer Data in accordance with our SOC 2 compliant Data
              Management Policy and this Privacy Policy.
            </p>

            <p>
              If you have any questions regarding the processing of Customer
              Data, please contact us at notices@openpipe.ai.
            </p>

            <h3>Information automatically collected</h3>
            <p>
              <em>
                In Short: Some information — such as your Internet Protocol (IP)
                address and/or browser and device characteristics — is collected
                automatically when you visit our Services.
              </em>
            </p>

            <p>
              We automatically collect certain information when you visit, use,
              or navigate on OpenPipe. This may include device and usage
              information, such as your IP address, browser and device
              characteristics, operating system, language preferences, referring
              URLs, device name, country, location, information about how and
              when you use our Services, and other technical information. This
              information is primarily needed to maintain the security and
              operation of our Services, and for our internal analytics and
              reporting purposes.
            </p>

            <p>The information we may collect includes:</p>

            <p>
              <strong>Log and Usage Data.</strong> Log and usage data is
              service-related, diagnostic, usage, and performance information
              our servers automatically collect when you access or use our
              Services and which we record in log files. Depending on how you
              interact with us, this log data may include your IP address,
              device information, browser type, and settings and information
              about your activity in the Services (such as the date/time stamps
              associated with your usage, pages and files viewed, searches, and
              other actions you take such as which features you use), device
              event information (such as system activity, error reports
              (sometimes called "crash dumps"), and hardware settings).
            </p>

            <p>
              <strong>Device Data.</strong> We collect device data such as
              information about your computer, phone, tablet, or other device
              you use to access the Services. Depending on the device used, this
              device data may include information such as your IP address (or
              proxy server), device and application identification numbers,
              location, browser type, hardware model, Internet service provider
              and/or mobile carrier, operating system, and system configuration
              information.
            </p>

            <p>
              <strong>Location Data.</strong> We collect location data such as
              information about your device's location, which can be either
              precise or imprecise. How much information we collect depends on
              the type and settings of the device you use to access the
              Services. For example, we may use GPS and other technologies to
              collect geolocation data that tells us your current location
              (based on your IP address). You can opt out of allowing us to
              collect this information either by refusing access to the
              information or by disabling your Location setting on your device.
              However, if you choose to opt out, you may not be able to use
              certain aspects of the Services.
            </p>

            <h2 id="section2">2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
            <p>
              We process your personal information for a variety of reasons,
              depending on how you interact with our Services, including:
            </p>

            <ul>
              <li>
                To facilitate account creation and authentication and otherwise
                manage user accounts. We may process your information so you can
                create and log in to your account, as well as keep your account
                in working order.
              </li>
              <li>
                To deliver and facilitate delivery of Services to the user. We
                may process your information to provide you with the requested
                service.
              </li>
              <li>
                To respond to user inquiries/offer support to users. We may
                process your information to respond to your inquiries and solve
                any potential issues you might have with the requested service.
              </li>
              <li>
                To send administrative information to you. We may process your
                information to send you details about our products and services,
                changes to our terms and policies, and other similar
                information.
              </li>
              <li>
                To fulfill and manage your orders. We may process your
                information to fulfill and manage your orders, payments,
                returns, and exchanges made through the Services.
              </li>
              <li>
                To enable user-to-user communications. We may process your
                information if you choose to use any of our offerings that allow
                for communication with another user.
              </li>
              <li>
                To request feedback. We may process your information when
                necessary to request feedback and to contact you about your use
                of our Services.
              </li>
              <li>
                To send you marketing and promotional communications. We may
                process the personal information you send to us for our
                marketing purposes, if this is in accordance with your marketing
                preferences. You can opt out of our marketing emails at any
                time.
              </li>
              <li>
                To protect our Services. We may process your information as part
                of our efforts to keep our Services safe and secure, including
                fraud monitoring and prevention.
              </li>
              <li>
                To identify usage trends. We may process information about how
                you use our Services to better understand how they are being
                used so we can improve them.
              </li>
              <li>
                To determine the effectiveness of our marketing and promotional
                campaigns. We may process your information to better understand
                how to provide marketing and promotional campaigns that are most
                relevant to you.
              </li>
              <li>
                To save or protect an individual's vital interest. We may
                process your information when necessary to save or protect an
                individual's vital interest, such as to prevent harm.
              </li>
            </ul>

            <h3>Lawful Basis for Processing Specific Categories of Data</h3>
            <p>
              In accordance with applicable data protection laws, including the
              General Data Protection Regulation (GDPR) in the European Union,
              we process certain categories of personal data only when we have a
              lawful basis to do so. For specific categories of data, such as
              sensitive personal data, the legal grounds for processing are
              outlined below:
            </p>

            <ul>
              <li>
                <strong>Special Categories of Data:</strong> We may process
                special categories of personal data, such as health information,
                racial or ethnic origin, religious beliefs, or sexual
                orientation, only where we have explicit consent from you or
                where processing is necessary for specific purposes as outlined
                by law.
              </li>
              <li>
                <strong>Health Data:</strong> If we process health-related data,
                it is typically for the purpose of providing services to you,
                ensuring your safety, or fulfilling legal obligations. This
                processing is carried out only with your explicit consent or
                where it is necessary for the establishment, exercise, or
                defense of legal claims.
              </li>
              <li>
                <strong>Legal Obligations:</strong> We may also process special
                categories of data to comply with legal obligations, such as
                those relating to employment law or public health requirements.
              </li>
              <li>
                <strong>Public Interest:</strong> In certain cases, we may
                process special categories of data if it is necessary for
                reasons of substantial public interest, provided that such
                processing is carried out in accordance with applicable laws and
                with appropriate safeguards.
              </li>
              <li>
                <strong>Vital Interests:</strong> Processing of special
                categories of data may be carried out when it is necessary to
                protect your vital interests or the vital interests of another
                person, especially in emergency situations.
              </li>
              <li>
                <strong>Employment:</strong> For data related to employment, we
                process such information as part of fulfilling employment
                contracts, ensuring workplace safety, and meeting other legal
                obligations related to employment law.
              </li>
            </ul>

            <p>
              We ensure that any processing of special categories of data is
              conducted with the highest level of care and in compliance with
              all relevant data protection regulations.
            </p>

            <p>
              If you have any questions regarding the processing of specific
              categories of data or if you wish to exercise your rights in
              relation to such data, please contact us at notices@openpipe.ai.
            </p>

            <h3>Data Processing Role</h3>
            <h4>1. Introduction</h4>
            <p>
              In our capacity as a data processor, we handle personal data on
              behalf of our clients, who are the data controllers. This means we
              process personal data according to the instructions provided by
              our clients and in accordance with the terms of our contractual
              agreements.
            </p>

            <h4>2. Responsibilities as a Data Processor</h4>
            <p>
              As a data processor, we undertake the following responsibilities:
            </p>

            <ul>
              <li>
                <strong>Data Processing Activities:</strong> We process personal
                data only in accordance with the documented instructions
                provided by our clients. These instructions will be detailed in
                our data processing agreements.
              </li>
              <li>
                <strong>Security Measures:</strong> We implement appropriate
                technical and organizational measures to ensure the security of
                personal data and protect it against unauthorized access,
                disclosure, alteration, or destruction.
              </li>
              <li>
                <strong>Sub-processors:</strong> We may engage third-party
                sub-processors to carry out certain processing activities. We
                ensure that any sub-processors we use are bound by similar data
                protection obligations as those in our agreement with our
                clients. Clients will be informed of any new sub-processors.
              </li>
              <li>
                <strong>Data Breach Notification:</strong> In the event of a
                personal data breach, we will notify our clients without undue
                delay and provide all relevant information to assist them in
                fulfilling their own legal obligations regarding breach
                notifications.
              </li>
              <li>
                <strong>Data Subject Rights:</strong> While we process personal
                data on behalf of our clients, we do not handle requests from
                data subjects directly. Instead, we will assist our clients in
                responding to such requests, including providing access to
                personal data or facilitating the correction or deletion of
                data, as required.
              </li>
              <li>
                <strong>Data Retention and Deletion:</strong> We retain personal
                data only for as long as necessary to fulfill the purposes
                outlined in our data processing agreements. Upon termination of
                our services or as otherwise instructed by our clients, we will
                securely delete or return all personal data in accordance with
                contractual requirements.
              </li>
              <li>
                <strong>Compliance:</strong> We comply with applicable data
                protection laws and regulations in our role as a data processor.
                We will assist our clients in meeting their obligations under
                these laws, including conducting data protection impact
                assessments where necessary.
              </li>
            </ul>

            <h3>How We Process Your Training Data</h3>
            <p>
              Training Data (as defined above) is not used for any model
              training of any kind, both on behalf of the Company, or on behalf
              of other customers of the Company who are not directly connected
              to the Training Data, except for the specific models trained on
              behalf of the Customer who supplied the Training Data.
            </p>

            <p>
              If you have any questions regarding the processing of Training
              Data, please contact us at notices@openpipe.ai.
            </p>

            <h2 id="section3">
              3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <p>
              <strong>
                Vendors, Consultants, and Other Third-Party Service Providers.
              </strong>{" "}
              We may share your data with third-party vendors, service
              providers, contractors, or agents ("third parties") who perform
              services for us or on our behalf and require access to such
              information to do that work. We have contracts in place with our
              third parties, which are designed to help safeguard your personal
              information. This means that they cannot do anything with your
              personal information unless we have instructed them to do it. They
              will also not share your personal information with any
              organization apart from us. They also commit to protect the data
              they hold on our behalf and to retain it for the period we
              instruct. The categories of third parties we may share personal
              information with are as follows:
            </p>

            <ul>
              <li>Cloud Computing Services</li>
              <li>Communication & Collaboration Tools</li>
              <li>Data Analytics Services</li>
              <li>Data Storage Service Providers</li>
              <li>Finance & Accounting Tools</li>
              <li>Government Entities</li>
              <li>Order Fulfillment Service Providers</li>
              <li>Payment Processors</li>
              <li>Performance Monitoring Tools</li>
              <li>Product Engineering & Design Tools</li>
              <li>Retargeting Platforms</li>
              <li>Sales & Marketing Tools</li>
              <li>Testing Tools</li>
              <li>User Account Registration & Authentication Services</li>
              <li>Website Hosting Service Providers</li>
            </ul>

            <p>
              We also may need to share your personal information in the
              following situations:
            </p>

            <ul>
              <li>
                <strong>Business Transfers.</strong> We may share or transfer
                your information in connection with, or during negotiations of,
                any merger, sale of company assets, financing, or acquisition of
                all or a portion of our business to another company.
              </li>
              <li>
                <strong>Business Partners.</strong> We may share your
                information with our business partners to offer you certain
                products, services, or promotions.
              </li>
            </ul>

            <h2 id="section4">
              4. IS YOUR INFORMATION TRANSFERRED INTERNATIONALLY?
            </h2>
            <p>
              We operate and may process information in multiple jurisdictions,
              service providers, and other third parties may be located or
              process information outside of the jurisdiction in which you
              reside. In such cases, your information may be collected, used,
              disclosed, stored, and processed in these other jurisdictions for
              the purposes described in this privacy notice. The data protection
              and other laws of the United States might differ from your
              jurisdiction. In addition, your information may be subject to the
              laws of those other jurisdictions, including lawful requirements
              to disclose information to government authorities. We are
              committed to your privacy rights and if you want to know what we
              are doing to comply with rights that may be applicable to you,
              please contact us as the details set out below.
            </p>

            <p>
              To the extent information including information from the EU and
              EEA is transferred outside of those jurisdictions by your use of
              the Services, we will adopt standard contractual clauses or other
              means pertaining to data protection approved in your country or
              region, and transfer the information to a third country according
              to the requirements of applicable laws.
            </p>

            <h2 id="section5">5. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
            <p>
              We will only keep your information for as long as it is necessary
              to provide you with Services or for other purposes as described in
              this privacy notice, unless a longer retention period is required
              or permitted by law (such as tax, accounting or other legal
              requirements). No purpose in this privacy notice (other than as
              may be required by law) will require us keeping your information
              for longer than 1 year.
            </p>

            <p>
              When we have no ongoing legitimate business need to process your
              information, we will either delete or anonymize such information,
              or, if this is not possible (for example, because your information
              has been stored in backup archives), then we will securely store
              your information and isolate it from any further processing until
              deletion is possible.
            </p>

            <p>
              Customer Data (as defined above) is retained and securely disposed
              of in accordance with our SOC 2 compliant Data Management Policy.
              Customer Data is securely disposed of within: (i) 60 days after
              termination of any Cloud Service Agreement between Company and
              Customer, or (ii) 30 days after such data is marked for deletion
              by Customer. If you have any questions regarding the retainment or
              disposal of Customer Data, please contact us at
              notices@openpipe.ai.
            </p>

            <h2 id="section6">6. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
            <p>
              Customer Data, including but not limited to Training Data and
              Custom Models, is private and is not shared with any other
              customers. Customer Data is not public and access to such data
              through our online service or API requires authorization.
            </p>

            <p>
              We have implemented appropriate and reasonable technical and
              organizational security measures designed to protect the security
              of any personal information and Customer Data we process. However,
              despite our safeguards and efforts to secure your information, no
              electronic transmission over the Internet or information storage
              technology can be guaranteed to be 100% secure, so we cannot
              promise or guarantee that hackers, cybercriminals, or other
              unauthorized third parties will not be able to defeat our security
              and improperly collect, access, steal, or modify your information.
              Although we will do our best to protect your personal information,
              transmission of personal information to and from our Services is
              at your own risk. You should only access the Services within a
              secure environment.
            </p>

            <h2 id="section7">7. DO WE COLLECT INFORMATION FROM MINORS?</h2>
            <p>
              We do not knowingly solicit data from or market to children under
              18 years of age. By using the Services, you represent that you are
              at least 18 or that you are the parent or guardian of such a minor
              and consent to such minor dependent's use of the Services. If we
              learn that personal information from users less than 18 years of
              age has been collected, we will deactivate the account and take
              reasonable measures to promptly delete such data from our records.
              If you become aware of any data we may have collected from
              children under age 18, please contact us at notices@openpipe.ai.
            </p>

            <h2 id="section8">8. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
            <p>
              As a data subject, you have certain rights concerning your
              personal data under applicable data protection laws. We are
              committed to ensuring that you can exercise these rights
              effectively. Below is a summary of the rights you have and how you
              can exercise them:
            </p>

            <ol>
              <li>
                <strong>Right to Restrict Processing</strong>
                <br />
                You have the right to request the restriction of processing of
                your personal data under certain circumstances, such as when you
                contest the accuracy of the data, or if the processing is
                unlawful and you oppose erasure.
              </li>

              <li>
                <strong>Right to Data Portability</strong>
                <br />
                You have the right to request that we transfer your personal
                data to another organization, or directly to you, in a
                structured, commonly used, and machine-readable format.
              </li>

              <li>
                <strong>Right to Object</strong>
                <br />
                You have the right to object to the processing of your personal
                data in certain situations, including when the processing is
                based on legitimate interests or for direct marketing purposes.
              </li>
            </ol>

            <p>
              We strive to provide you with the ability to access, review,
              correct, update, suppress, or otherwise limit our use of the
              information you have previously provided directly to us,
              including:
            </p>

            <p>
              <strong>Via Your Account Settings.</strong> If you have registered
              with our Services, you may have the ability to access and use
              certain features and functionality that provide you with the
              ability to set certain privacy, permissions, and account options.
              Please be aware that if you limit the collection of certain
              information, you may not be able to use all of the features and
              functionality of the Services.
            </p>

            <p>
              <strong>Via Email/Mail.</strong> You may also contact us by email
              using the contact information under the "Contact Us" heading
              below. In your request, please specify clearly what information
              you would like to access, change, update, suppress, or delete.
            </p>

            <p>Additionally, you may opt-out of:</p>

            <p>
              <strong>Electronic Communications From Us.</strong> If you no
              longer want to receive marketing-related emails from us on a
              going-forward basis, you may opt-out of receiving these
              marketing-related emails by (a) contacting us via email in
              accordance with the procedure set forth above, and/or (b) using
              the opt-out mechanism that is contained in each such email. Please
              note that there are certain service notification emails that you
              may not opt-out of, such as notifications of changes to the
              Services or policies and emails related to the programs and
              Services you are involved in.
            </p>

            <p>
              <strong>SMS messaging.</strong> You may terminate SMS messaging by
              replying "STOP". SMS messaging will be terminated promptly unless
              you provide subsequent consent to receive messages.
            </p>

            <p>
              <strong>Third Party Marketing Purposes.</strong> You may request
              that we do not disclose your information on a going-forward basis
              with third parties for their direct marketing purposes by
              contacting us via email in accordance with the procedure set forth
              above.
            </p>

            <p>
              For any of the foregoing requests, we may request certain
              information to verify your identity. For all requests, we will
              strive to respond as soon as reasonably practicable.
            </p>

            <h2 id="section9">9. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
            <p>
              Most web browsers and some mobile operating systems and mobile
              applications include a Do-Not-Track ("DNT") feature or setting you
              can activate to signal your privacy preference not to have data
              about your online browsing activities monitored and collected. At
              this stage no uniform technology standard for recognizing and
              implementing DNT signals has been finalized. As such, we do not
              currently respond to DNT browser signals or any other mechanism
              that automatically communicates your choice not to be tracked
              online. If a standard for online tracking is adopted that we must
              follow in the future, we will inform you about that practice in a
              revised version of this privacy notice.
            </p>

            <h2 id="section10">10. OPENPIPE AND STATE LAWS</h2>
            <p>
              Under the California Consumer Privacy Act as amended by the
              California Privacy Rights Act (the "CCPA"), California residents
              have additional rights to what is set out in this privacy notice
              which impact how your information is being processed:
            </p>

            <p>
              As described in this privacy notice, we collect various types of
              information about you in order to fulfill specific business
              purposes, including for performance of business operations,
              management and administration, and legal and regulatory
              compliance. We do not use your information for commercial
              purposes.
            </p>

            <p>
              Subject to exceptions detailed in the CCPA, as a California
              resident, you have the right to request: (i) deletion of your
              information, (ii) correction of inaccurate information (iii) to
              know / access the categories of information that we collect about
              you, including the specific pieces of information; and (v) to know
              the categories of information disclosed for a business purpose.
            </p>

            <h3>Submitting a request</h3>
            <p>
              You may ask us to disclose certain information to you about our
              collection and use of your information over the past 12 months.
              Once we receive and confirm your identity as a user of our
              Service, we can disclose that information to you. You may also
              make a request to have that information erased or deleted.
            </p>

            <p>
              You may make these requests by contacting us at the details set
              out below.
            </p>

            <p>
              You may make a request for your information twice every twice
              every 12 months. If you are making an erasure request, it will
              help us if you include details of the information you would like
              erased. We may need to ask for additional information to fully
              identify you. If we can't identify you, we may not be able to
              erase your information.
            </p>

            <p>
              Please note that if you request that we remove your information,
              we may retain some of the information for specific reasons, such
              as to resolve disputes, troubleshoot problems, and as required by
              law.
            </p>

            <p>
              Furthermore, some information is never completely removed from our
              databases due to technical constraints and the fact that we
              regularly back up our systems. Therefore, not all of your
              information will be completely removed from our databases.
            </p>

            <p>
              We will respond to you on these requests as quickly as received
              your note but it may take us up to 45 days. If we need more time
              than that, we will let you know.
            </p>

            <p>
              We will not discriminate against you for submitting a right
              request.
            </p>

            <h3>Virginia, Colorado, Connecticut or Utah Residents</h3>
            <p>
              As residents of the above-named states, you also have rights under
              each of the following laws:
            </p>

            <ul>
              <li>
                The Virginia Consumer Data Privacy Act and any regulations,
                amendments and/or updates thereto;
              </li>
              <li>
                The Colorado Data Privacy Act and any regulations, amendments
                and/or updates thereto;
              </li>
              <li>
                The Connecticut Act Concerning Personal Data Privacy and Online
                Monitoring and any regulations, amendments and/or updates
                thereto; and
              </li>
              <li>
                The Utah Consumer Privacy Act and any regulations, amendments
                and/or updates thereto,
              </li>
            </ul>

            <p>
              As a user of our Services, you can request that we provide you
              information to:
            </p>

            <ul>
              <li>
                Conduct and document data protection assessments about us;
              </li>
              <li>
                Ask us to demonstrate our compliance with our obligations under
                each of the state laws above named (to the extent we have
                determined they are applicable);
              </li>
              <li>
                Request that we implement appropriate technical and
                organizational measures and to assist you in the event of a data
                security and/or data breach notification.
              </li>
            </ul>

            <h2 id="section11">11. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
            <p>
              We may update this privacy notice from time to time. The updated
              version will be indicated by an updated "Revised" date and the
              updated version will be effective as soon as it is accessible. If
              we make material changes to this privacy notice, we may notify you
              either by prominently posting a notice of such changes or by
              directly sending you a notification. We encourage you to review
              this privacy notice frequently to be informed of how we are
              protecting your information.
            </p>

            <h2 id="section12">
              12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <p>
              If you have questions or comments about this notice, you may
              contact us by email at notices@openpipe.ai.
            </p>

            <h2 id="section13">
              13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
              YOU?
            </h2>
            <p>
              Based on the applicable laws of your country, you may have the
              right to request access to the personal information we collect
              from you, change that information, or delete it in some
              circumstances. To request to review, update, or delete your
              personal information, please email us at notices@openpipe.ai.
            </p>

            <h2 id="section14">14. MISCELLANEOUS</h2>
            <p>
              When you visit or log in to our website, cookies and similar
              technologies may be used by our online data partners or vendors to
              associate these activities with other personal information they or
              others have about you, including by association with your email or
              home address. We (or service providers on our behalf) may then
              send communications and marketing to these email or home
              addresses. You may opt out of receiving this advertising by
              visiting https://app.retention.com/optout
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Privacy;
